import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import TestProceduresManagement from './components/TestProceduresManagement';
import ContactMessagesManagement from './components/ContactMessagesManagement';
import VideoManagementSection from './components/VideoManagementSection';
import ProductImageUploadBox from './components/ProductImageUploadBox';
import { LogoWhite } from '../../components/Logo';

import { API_URL } from '../../utils/apiUrl';
import { validatePlaybackUrl, validateThumbnailUrl } from '../../utils/videoUrlValidation';

// Canonical product categories — kept aligned with TEST_PROCEDURE_CATEGORIES
// in backend/server.py. Backend resolve_procedure_category() also accepts
// legacy aliases (e.g. "Giyim", "Tekstil" -> "Giyim / Tekstil").
const CATEGORIES = [
  "Elektronik", "Giyim / Tekstil", "Kozmetik", "Ev Ürünleri",
  "Aksesuar", "Kişisel Bakım", "Gıda", "Diğer",
];

const STATUS_LABELS = {
  new_application: "Yeni Başvuru",
  under_review: "İnceleme Altında",
  awaiting_product: "Ürün Bekleniyor",
  product_received: "Ürün Teslim Alındı",
  testing: "Test Ediliyor",
  result_ready: "Sonuç Hazır",
  badge_payment_pending: "Güven Rozeti Ödeme Bekleniyor",
  badge_active: "Güven Rozeti Aktif",
  premium_active: "Premium Aktif",
  expired: "Süresi Doldu",
  retest_required: "Yeniden Test Gerekli",
  cancelled: "İptal Edildi",
};


// ── Products ──
const ProductsManagement = () => {
  const { hasPerm, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [vendorUsers, setVendorUsers] = useState([]);
  const [legacyVendors, setLegacyVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const emptyForm = {
    name: '', brand: '', category: '', score: '', description: '',
    // Legacy: kept for backward-compat rendering only. NEW products never set these via UI.
    video_url: '', video_type: 'embed',
    image_url: '', image_source: 'upload',
    // TT-PRODUCT-IMAGE-GALLERY-001: gallery (index 0 = main image). Default: empty, upload mode.
    images: [],
    badge_expiry_date: '', price: '',
    review_fee_status: 'unpaid', badge_subscription_status: 'inactive', vendor_user_id: '',
    is_featured: false, featured_order: '',
    seller_whatsapp_phone: '', seller_instagram_url: '', seller_website_url: '',
    // Güvenİzi audit (only used when editing & score changes)
    change_reason: '', source: '', admin_note: '',
    // TT-VIDEO-001: nested video metadata (Bunny/CDN-ready)
    video: null,
  };
  const [formData, setFormData] = useState(emptyForm);
  const [verifyLinks, setVerifyLinks] = useState({});
  const [perfModal, setPerfModal] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [perfRange, setPerfRange] = useState('7d');
  const [perfLoading, setPerfLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  // TT-ROZET-001-FINAL — badge activation modal state (product being activated + selected date)
  const [badgeActivateModal, setBadgeActivateModal] = useState(null); // { productId, productName } | null
  const [badgeActivateDate, setBadgeActivateDate] = useState('');
  const [badgeActivateBusy, setBadgeActivateBusy] = useState(false);

  // TT-ROZET-001-FINAL — real-time badge validity for the "Rozet" column
  const computeBadgeStatus = (p) => {
    if (!p) return { label: 'Pasif', cls: 'bg-neutral-100 text-neutral-600' };
    if (!p.badge_active) return { label: 'Pasif', cls: 'bg-neutral-100 text-neutral-600' };
    const raw = p.badge_expires_at;
    if (!raw) return { label: 'Süresi Doldu', cls: 'bg-red-100 text-red-800' };
    const exp = new Date(raw);
    if (isNaN(exp.getTime()) || exp <= new Date()) {
      return { label: 'Süresi Doldu', cls: 'bg-red-100 text-red-800' };
    }
    const dd = String(exp.getDate()).padStart(2, '0');
    const mm = String(exp.getMonth() + 1).padStart(2, '0');
    const yyyy = exp.getFullYear();
    return { label: `Aktif — ${dd}.${mm}.${yyyy}`, cls: 'bg-green-100 text-green-800' };
  };

  const openBadgeActivateModal = (product) => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setBadgeActivateDate(iso);
    setBadgeActivateModal({ productId: product.id, productName: product.name });
  };

  const confirmBadgeActivate = async () => {
    if (!badgeActivateModal || !badgeActivateDate) return;
    setBadgeActivateBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/products/${badgeActivateModal.productId}/activate-badge`,
        { expires_at: badgeActivateDate },
      );
      toast.success('Rozet aktive edildi');
      setBadgeActivateModal(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rozet aktivasyonu başarısız');
    } finally {
      setBadgeActivateBusy(false);
    }
  };

  useEffect(() => {
    const fetches = [axios.get(`${API_URL}/api/products`).then(r => r.data).catch(() => [])];
    if (user?.role === 'super_admin') {
      // TT-VIDEO-003: clean vendor list (test/demo filtered, deduped)
      fetches.push(
        axios.get(`${API_URL}/api/admin/product-vendor-options`)
          .then(r => r.data)
          .catch(() => ({ vendors: [], legacy_associations: [] }))
      );
    }
    Promise.all(fetches).then(([prods, vendorData]) => {
      setProducts(prods);
      if (vendorData) {
        setVendorUsers(vendorData.vendors || []);
        setLegacyVendors(vendorData.legacy_associations || []);
      }
      prods.filter(p => p.score >= 7).forEach(p => fetchVerifyLink(p.id));
    }).finally(() => setLoading(false));
  }, [user]);

  const fetchProducts = async () => {
    const { data } = await axios.get(`${API_URL}/api/products`);
    setProducts(data);
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate seller fields (super admin only) — lenient, backend normalizes
    if (user?.role === 'super_admin') {
      const errors = {};
      const wp = formData.seller_whatsapp_phone.trim();
      if (wp) {
        const digits = wp.replace(/\D/g, '');
        // Accept: 5XXXXXXXXX, 05XXXXXXXXX, 905XXXXXXXXX, +905XXXXXXXXX
        const valid = (digits.length === 10 && digits.startsWith('5')) ||
                      (digits.length === 11 && digits.startsWith('05')) ||
                      (digits.length === 12 && digits.startsWith('905'));
        if (!valid) {
          errors.seller_whatsapp_phone = 'Ge\u00E7ersiz numara. \u00D6rnek: 5325194229, 05325194229, 905325194229';
        }
      }
      const ig = formData.seller_instagram_url.trim();
      if (ig) {
        // Accept bare domains like instagram.com/user — auto-prefix https:// for URL parsing
        const igTestUrl = ig.startsWith('http://') || ig.startsWith('https://') ? ig : 'https://' + ig;
        try {
          const igUrl = new URL(igTestUrl);
          const host = igUrl.hostname.toLowerCase();
          if (host !== 'instagram.com' && !host.endsWith('.instagram.com')) {
            errors.seller_instagram_url = 'Ge\u00E7erli bir Instagram adresi giriniz';
          }
        } catch {
          errors.seller_instagram_url = 'Ge\u00E7erli bir Instagram adresi giriniz';
        }
      }
      const ws = formData.seller_website_url.trim();
      if (ws) {
        // Accept bare domains like eticaretduayeni.com — auto-prefix https:// for URL parsing
        const wsTestUrl = ws.startsWith('http://') || ws.startsWith('https://') ? ws : 'https://' + ws;
        try {
          const wsUrl = new URL(wsTestUrl);
          if (!['http:', 'https:'].includes(wsUrl.protocol)) {
            errors.seller_website_url = 'Ge\u00E7ersiz protokol';
          } else if (!wsUrl.hostname || !wsUrl.hostname.includes('.')) {
            errors.seller_website_url = 'Ge\u00E7erli bir alan ad\u0131 giriniz';
          }
        } catch {
          errors.seller_website_url = 'Ge\u00E7erli bir web sitesi adresi giriniz';
        }
      }
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        toast.error('Sat\u0131c\u0131 alanlar\u0131nda hata var');
        return;
      }
    }
    setFormErrors({});

    // TT-VIDEO-002: block save if video URLs violate the strict whitelist.
    if (formData.video && typeof formData.video === 'object') {
      const pbErr = formData.video.playbackUrl ? validatePlaybackUrl(formData.video.playbackUrl) : null;
      const thErr = formData.video.thumbnailUrl ? validateThumbnailUrl(formData.video.thumbnailUrl) : null;
      if (pbErr) { toast.error(pbErr); return; }
      if (thErr) { toast.error(thErr); return; }
      // TT-VIDEO-003-ADDON: video duration must be a positive number when provided
      const dur = formData.video.durationSeconds;
      if (dur !== null && dur !== undefined && dur !== '') {
        const durNum = Number(dur);
        if (!Number.isFinite(durNum) || durNum < 0) {
          toast.error('Video süresi saniye cinsinden pozitif bir sayı olmalıdır.');
          return;
        }
      }
      if (formData.video.status === 'PUBLISHED') {
        if (!formData.video.playbackUrl) {
          toast.error('Bu video kaynağı desteklenmiyor. Lütfen Bunny/CDN üzerinden alınmış geçerli bir video oynatma bağlantısı girin.');
          return;
        }
        if (!formData.video.thumbnailUrl) {
          toast.error('Video kapak görseli bağlantısı geçerli değil. Lütfen Bunny/CDN üzerinden alınmış geçerli bir kapak görseli bağlantısı girin.');
          return;
        }
      }
    }
    try {
      const payload = { ...formData, score: parseFloat(formData.score) };
      payload.featured_order = formData.featured_order !== '' ? parseInt(formData.featured_order) : null;
      // Normalize price: accept Turkish formats like 1.200,50 or ₺1200
      const rawPrice = formData.price.toString().replace(/[₺\s]/g, '').replace(/\./g, '').replace(',', '.');
      payload.price = rawPrice !== '' ? parseFloat(rawPrice) : null;
      // TT-PRODUCT-IMAGE-GALLERY-001: sync payload.images + main image_url based on source.
      if (formData.image_source === 'upload') {
        const gallery = Array.isArray(formData.images) ? formData.images.slice(0, 5) : [];
        payload.images = gallery;
        payload.image_url = gallery[0] || null;
      } else {
        // External URL mode: no gallery.
        payload.images = [];
        payload.image_url = (formData.image_url || '').trim() || null;
      }
      if (!hasPerm('can_view_payments')) {
        delete payload.review_fee_status;
        delete payload.badge_subscription_status;
      }
      // Only super admin can set seller fields; strip for others
      if (user?.role !== 'super_admin') {
        delete payload.seller_whatsapp_phone;
        delete payload.seller_instagram_url;
        delete payload.seller_website_url;
      } else {
        // Send empty string to clear, trimmed value to set — never null (null = "don't update")
        payload.seller_whatsapp_phone = formData.seller_whatsapp_phone.trim();
        payload.seller_instagram_url = formData.seller_instagram_url.trim();
        payload.seller_website_url = formData.seller_website_url.trim();
      }

      // Güvenİzi: include audit fields only when editing AND score changed
      const scoreChanged = editingProduct && parseFloat(editingProduct.score) !== payload.score;
      if (scoreChanged) {
        if (!formData.change_reason.trim() || !formData.source) {
          toast.error('Puan değişikliği için "Değişiklik nedeni" ve "Kaynak" zorunludur.');
          return;
        }
        payload.change_reason = formData.change_reason.trim();
        payload.source = formData.source;
        payload.admin_note = formData.admin_note.trim() || null;
      } else {
        // Strip audit fields when not changing — avoid sending stale data
        delete payload.change_reason;
        delete payload.source;
        delete payload.admin_note;
      }

      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, payload);
        toast.success('\u00DCr\u00FCn g\u00FCncellendi');
      } else {
        await axios.post(`${API_URL}/api/products`, payload);
        toast.success('\u00DCr\u00FCn eklendi');
      }
      setShowForm(false); setEditingProduct(null); setFormData(emptyForm);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || '\u0130\u015Flem ba\u015Far\u0131s\u0131z');
    }
  };

  const handleEdit = (product) => {
    if (!hasPerm('can_edit_product')) { toast.error('Düzenleme yetkiniz yok'); return; }
    setEditingProduct(product);
    setFormErrors({});
    // TT-PRODUCT-IMAGE-GALLERY-001: hydrate gallery. Prefer product.images; fall back to
    // legacy single-image field. Only pre-select 'url' source when the product genuinely
    // uses an external URL with no uploaded gallery.
    const hydratedImages = Array.isArray(product.images) && product.images.length > 0
      ? product.images.slice(0, 5)
      : (product.image_url && product.image_source === 'upload' ? [product.image_url] : []);
    const usesExternalUrl = product.image_source === 'url'
      && product.image_url
      && !hydratedImages.length;
    setFormData({
      name: product.name, brand: product.brand, category: product.category,
      score: product.score.toString(), description: product.description,
      video_url: product.video_url || '', video_type: product.video_type || 'embed',
      image_url: product.image_url || '',
      image_source: usesExternalUrl ? 'url' : 'upload',
      images: hydratedImages,
      badge_expiry_date: product.badge_expiry_date || '', price: product.price != null ? product.price.toString() : '',
      review_fee_status: product.review_fee_status || 'unpaid',
      badge_subscription_status: product.badge_subscription_status || 'inactive',
      vendor_user_id: product.vendor_user_id || '',
      is_featured: product.is_featured || false,
      featured_order: product.featured_order ?? '',
      seller_whatsapp_phone: product.seller_whatsapp_phone || '',
      seller_instagram_url: product.seller_instagram_url || '',
      seller_website_url: product.seller_website_url || '',
      // Reset Güvenİzi audit fields each time edit is opened
      change_reason: '', source: '', admin_note: '',
      // TT-VIDEO-001: hydrate nested video metadata
      video: product.video || null,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!hasPerm('can_delete')) { toast.error('Silme yetkiniz yok'); return; }
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`);
      toast.success('Ürün silindi'); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Silme başarısız'); }
  };

  const togglePublish = async (product) => {
    if (!hasPerm('can_publish_product')) { toast.error('Yayınlama yetkiniz yok'); return; }
    try {
      await axios.put(`${API_URL}/api/products/${product.id}`, { published: !product.published });
      toast.success(product.published ? 'Yayından kaldırıldı' : 'Yayınlandı'); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleGenerateVerifyLink = async (productId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/verification-links/${productId}`);
      setVerifyLinks(prev => ({ ...prev, [productId]: data.slug }));
      toast.success('Doğrulama linki oluşturuldu');
    } catch (err) { toast.error(err.response?.data?.detail || 'Link oluşturulamadı'); }
  };

  const handleCopyVerifyLink = (slug) => {
    navigator.clipboard.writeText(`https://testettik.com/v/${slug}`);
    toast.success('Link kopyalandı');
  };

  const fetchVerifyLink = async (productId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/verification-links/${productId}`);
      if (data.exists) setVerifyLinks(prev => ({ ...prev, [productId]: data.slug }));
    } catch {}
  };

  const openPerfModal = async (product, range = '7d') => {
    setPerfModal(product);
    setPerfRange(range);
    setPerfLoading(true);
    setPerfData(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/products/${product.id}/verification-performance?range=${range}`);
      setPerfData(data);
    } catch { toast.error('Performans verileri y\u00FCklenemedi'); }
    finally { setPerfLoading(false); }
  };

  const changePerfRange = (range) => {
    if (perfModal) openPerfModal(perfModal, range);
  };

  const handleCopyReport = () => {
    if (!perfData || !perfModal) return;
    const rangeLabel = perfRange === '7d' ? 'Son 7 g\u00FCn' : perfRange === '30d' ? 'Son 30 g\u00FCn' : 'T\u00FCm zamanlar';
    const sourceLines = Object.entries(perfData.source_breakdown || {}).map(([k, v]) => `  ${k}: ${v}`).join('\n') || '  Veri yok';
    const destLines = Object.entries(perfData.destination_breakdown || {}).map(([k, v]) => `  ${k}: ${v}`).join('\n') || '  Veri yok';
    const text = `TestettiK\u00AE Do\u011Frulama Performans Raporu\n` +
      `\u00DCr\u00FCn: ${perfModal.name} (${perfModal.brand})\n` +
      `D\u00F6nem: ${rangeLabel}\n` +
      `Do\u011Frulama Linki: ${perfData.slug ? `https://testettik.com/v/${perfData.slug}` : 'Yok'}\n` +
      `---\n` +
      `G\u00F6r\u00FCnt\u00FClenme: ${perfData.total_views}\n` +
      `T\u0131klama: ${perfData.total_clicks}\n` +
      `CTR: %${perfData.ctr}\n` +
      `---\n` +
      `Kaynak Da\u011F\u0131l\u0131m\u0131:\n${sourceLines}\n` +
      `---\n` +
      `Hedef Da\u011F\u0131l\u0131m\u0131:\n${destLines}`;
    navigator.clipboard.writeText(text);
    toast.success('Rapor panoya kopyaland\u0131');
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-black tracking-tight" data-testid="products-title">Ürün Yönetimi</h2>
        {hasPerm('can_add_product') && (
          <button onClick={() => { setShowForm(true); setEditingProduct(null); setFormData(emptyForm); }}
            className="bg-neutral-900 text-white px-5 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition"
            data-testid="add-product-button">+ Yeni Ürün</button>
        )}
      </div>

      {showForm && (
        <div className="bg-neutral-50 border border-neutral-200 rounded-md p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">{editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Ürün Adı</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Marka</label>
              <input type="text" name="brand" value={formData.brand} onChange={handleChange} required
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-brand-input" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Kategori</label>
              <select name="category" value={formData.category} onChange={handleChange} required
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-category-input">
                <option value="">Seçin</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Puan (0-10)</label>
              <input type="number" name="score" value={formData.score} onChange={handleChange} required
                min="0" max="10" step="0.1" disabled={!hasPerm('can_score_product') && editingProduct}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm disabled:bg-neutral-100" data-testid="product-score-input" />
            </div>
            {/* ── Güvenİzi (Trust Trace) audit fields — appear when editing and score has changed ── */}
            {editingProduct && parseFloat(formData.score || '0') !== parseFloat(editingProduct.score || '0') && (
              <div className="md:col-span-2 border-2 border-amber-300 bg-amber-50/50 rounded-md p-4" data-testid="guveniz-audit-panel">
                <p className="text-xs font-black uppercase text-amber-800 mb-1 tracking-wide">Güvenİzi — Puan Değişikliği Kaydı</p>
                <p className="text-[11px] text-amber-700 mb-3">
                  Eski puan: <strong>{Number(editingProduct.score).toFixed(1)}</strong> → Yeni puan: <strong>{formData.score || '?'}</strong>. Aşağıdaki alanlar zorunludur.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-700">Değişiklik Nedeni *</label>
                    <input type="text" name="change_reason" value={formData.change_reason} onChange={handleChange}
                      placeholder="Örn: Yeni numune ile yeniden test edildi"
                      required
                      className="w-full border border-amber-400 rounded px-3 py-2 text-sm bg-white" data-testid="change-reason-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-700">Kaynak *</label>
                    <select name="source" value={formData.source} onChange={handleChange} required
                      className="w-full border border-amber-400 rounded px-3 py-2 text-sm bg-white" data-testid="source-input">
                      <option value="">Seçin</option>
                      <option value="İlk test">İlk test</option>
                      <option value="Yeniden test">Yeniden test</option>
                      <option value="Uzman onayı">Uzman onayı</option>
                      <option value="Yeni numune">Yeni numune</option>
                      <option value="Kalite iyileştirmesi">Kalite iyileştirmesi</option>
                      <option value="Sistem güncellemesi">Sistem güncellemesi</option>
                      <option value="Manuel düzeltme">Manuel düzeltme</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold mb-1 text-neutral-700">Yönetici Notu (opsiyonel)</label>
                    <textarea name="admin_note" value={formData.admin_note} onChange={handleChange} rows="2"
                      placeholder="Bu değişikliğin arka planına dair ek bilgi..."
                      className="w-full border border-amber-300 rounded px-3 py-2 text-sm bg-white" data-testid="admin-note-input" />
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Fiyat (TL, opsiyonel)</label>
              <input type="text" name="price" value={formData.price} onChange={handleChange}
                placeholder="1200 veya 1.200,50"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-price-input" />
              <p className="text-[10px] text-neutral-400 mt-1">Test sırasında görülen satış fiyatı</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold mb-1 text-neutral-600">Açıklama</label>
              <textarea name="description" value={formData.description} onChange={handleChange} required rows="3"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-description-input" />
            </div>
            {/* ── MEDYA — Ürün Görseli ── */}
            <div className="md:col-span-2 border border-neutral-200 rounded-md p-4 bg-white">
              <p className="text-xs font-black uppercase text-neutral-500 mb-4 tracking-wide">Medya — Ürün Görseli</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold mb-1 text-neutral-600">Görsel Kaynağı</label>
                  <select name="image_source" value={formData.image_source} onChange={handleChange}
                    className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="image-source-select">
                    <option value="upload">Görsel Yükle</option>
                    <option value="url">Harici URL</option>
                  </select>
                </div>
                {formData.image_source === 'url' && (
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-600">Görsel URL</label>
                    <input type="url" name="image_url" value={formData.image_url} onChange={handleChange}
                      placeholder="https://..."
                      className="w-full border border-neutral-300 rounded px-3 py-2 text-sm font-mono" data-testid="image-url-input" />
                    <p className="text-[10px] text-neutral-400 mt-1">Harici görsel bağlantısı (jpg, png, webp)</p>
                  </div>
                )}
                {formData.image_source === 'upload' && (
                  <ProductImageUploadBox
                    images={formData.images}
                    onChange={(nextImages) => setFormData(prev => ({
                      ...prev,
                      images: nextImages,
                      image_url: nextImages[0] || prev.image_url,
                    }))}
                  />
                )}
              </div>
            </div>

            {/* ── TT-VIDEO-003: Video Yönetimi (TestettiK) — sole active video workflow ── */}
            <div className="md:col-span-2">
              <VideoManagementSection
                productCategory={formData.category}
                productId={editingProduct?.id || null}
                video={formData.video}
                onChange={(patch) => setFormData(prev => ({ ...prev, video: patch }))}
              />
            </div>

            {/* ── Eski Video Alanı (Legacy — Kullanmayın) — read-only, only if legacy data exists ── */}
            {formData.video_url && (
              <details className="md:col-span-2 border border-dashed border-neutral-300 rounded-md p-3 bg-neutral-50" data-testid="legacy-video-section">
                <summary className="text-[11px] font-black uppercase tracking-wider cursor-pointer" style={{ color: 'var(--text-muted)' }}>
                  Eski Video Alanı (Legacy — Kullanmayın)
                </summary>
                <div className="mt-3 space-y-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    Bu alan yalnızca geriye dönük uyumluluk için gösteriliyor. Yeni video kaynağı olarak KULLANMAYIN. Bu alandaki YouTube/Vimeo bağlantısı yeni TestettiK Video Kanıtı sistemine kesinlikle beslenmez ve homepage &quot;Video Kanıt&quot; rozetini tetiklemez.
                  </p>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Eski Video URL (salt okunur)</label>
                    <input type="text" value={formData.video_url} readOnly
                      className="w-full border border-neutral-200 rounded px-3 py-2 text-xs font-mono bg-white text-neutral-500" data-testid="legacy-video-url-readonly" />
                  </div>
                </div>
              </details>
            )}

            {/* ── Satıcı Ata (TT-VIDEO-003 cleaned) ── */}
            {user?.role === 'super_admin' && (
              <div className="md:col-span-2" data-testid="product-vendor-block">
                <label className="block text-xs font-bold mb-1 text-neutral-600">Satıcı Ata (opsiyonel)</label>
                <select name="vendor_user_id" value={formData.vendor_user_id} onChange={handleChange}
                  className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-vendor-select">
                  <option value="">Satıcı Seç (opsiyonel)</option>
                  {/* Existing legacy/inactive association — shown so admin sees current state; not for new picks */}
                  {legacyVendors
                    .filter(v => v.id === formData.vendor_user_id)
                    .map(v => (
                      <option key={`legacy-${v.id}`} value={v.id}>
                        Mevcut ilişki: {v.name}{v.vendor_brand ? ` / ${v.vendor_brand}` : ''} (legacy / inaktif — yeni seçim için önerilmez)
                      </option>
                    ))}
                  {vendorUsers.map(v => {
                    const label = v.vendor_brand
                      ? `${v.name} / ${v.vendor_brand}`
                      : v.name;
                    const tail = v.email ? ` — ${v.email}` : '';
                    return <option key={v.id} value={v.id}>{`${label}${tail}`}</option>;
                  })}
                </select>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Opsiyonel. Ürünü kayıtlı bir satıcı hesabıyla ilişkilendirir. Satıcı seçilmezse aşağıdaki yönlendirme bilgileri (WhatsApp / Instagram / Web Sitesi) manuel olarak kullanılabilir.
                </p>
                {vendorUsers.length === 0 && legacyVendors.length === 0 && (
                  <p className="text-[11px] mt-1 font-semibold" style={{ color: '#B45309' }} data-testid="product-vendor-empty">
                    Kayıtlı gerçek satıcı bulunamadı. Bu ürünü kaydetmek için satıcı seçmek zorunda değilsiniz.
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Rozet Geçerlilik Tarihi</label>
              <input type="date" name="badge_expiry_date" value={formData.badge_expiry_date} onChange={handleChange}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
            </div>
            {hasPerm('can_view_payments') && (
              <>
                <div>
                  <label className="block text-xs font-bold mb-1 text-neutral-600">İnceleme Ücreti</label>
                  <select name="review_fee_status" value={formData.review_fee_status} onChange={handleChange}
                    className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-review-fee-input">
                    <option value="unpaid">Ödenmedi</option>
                    <option value="paid">Ödendi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 text-neutral-600">Rozet Abonelik</label>
                  <select name="badge_subscription_status" value={formData.badge_subscription_status} onChange={handleChange}
                    className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="product-badge-sub-input">
                    <option value="inactive">İnaktif</option>
                    <option value="active">Aktif</option>
                    <option value="expired">Süresi Doldu</option>
                  </select>
                </div>
              </>
            )}
            {/* ── SATICI İLETİŞİM BİLGİLERİ (Sadece Süper Admin) ── */}
            {user?.role === 'super_admin' && (
              <div className="md:col-span-2 border border-neutral-200 rounded-md p-4 bg-white">
                <p className="text-xs font-black uppercase text-neutral-500 mb-4 tracking-wide">Satıcı Yönlendirme Bilgileri</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-600">WhatsApp Telefon</label>
                    <input type="text" name="seller_whatsapp_phone" value={formData.seller_whatsapp_phone} onChange={handleChange}
                      placeholder="5XXXXXXXXX"
                      className={`w-full border rounded px-3 py-2 text-sm ${formErrors.seller_whatsapp_phone ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                      data-testid="seller-whatsapp-input" />
                    {formErrors.seller_whatsapp_phone && <p className="text-[10px] text-red-500 mt-1">{formErrors.seller_whatsapp_phone}</p>}
                    <p className="text-[10px] text-neutral-400 mt-1">10 haneli, 5 ile başlayan numara</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-600">Instagram URL</label>
                    <input type="text" name="seller_instagram_url" value={formData.seller_instagram_url} onChange={handleChange}
                      placeholder="https://instagram.com/..."
                      className={`w-full border rounded px-3 py-2 text-sm ${formErrors.seller_instagram_url ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                      data-testid="seller-instagram-input" />
                    {formErrors.seller_instagram_url && <p className="text-[10px] text-red-500 mt-1">{formErrors.seller_instagram_url}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-neutral-600">Web Sitesi URL</label>
                    <input type="text" name="seller_website_url" value={formData.seller_website_url} onChange={handleChange}
                      placeholder="https://..."
                      className={`w-full border rounded px-3 py-2 text-sm ${formErrors.seller_website_url ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                      data-testid="seller-website-input" />
                    {formErrors.seller_website_url && <p className="text-[10px] text-red-500 mt-1">{formErrors.seller_website_url}</p>}
                  </div>
                </div>
                <p className="text-[10px] text-neutral-400 mt-3">Öncelik sırası: WhatsApp &gt; Instagram &gt; Web Sitesi. Doğrulama sayfasında sadece biri gösterilir.</p>
              </div>
            )}
            {/* ── ANASAYFA ÖNE ÇIKARMA (Devre dışı — slot sistemi ile kontrol edilir) ── */}
            {/* Anasayfa kontrolü kaldırıldı. Ana sayfa yalnızca slot atama sistemi üzerinden yönetilir. */}
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button type="submit" className="bg-neutral-900 text-white px-6 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition"
                data-testid="save-product-button">Kaydet</button>
              <button type="button" onClick={() => { setShowForm(false); setEditingProduct(null); }}
                className="border border-neutral-300 px-6 py-2.5 text-sm font-bold rounded hover:bg-neutral-100 transition">İptal</button>
            </div>
          </form>
        </div>
      )}

      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Ürün</th>
                <th className="text-left p-3 font-bold">Marka</th>
                <th className="text-left p-3 font-bold">Puan</th>
                <th className="text-left p-3 font-bold">Sonuç</th>
                <th className="text-left p-3 font-bold">Yayın</th>
                {hasPerm('can_view_payments') && <th className="text-left p-3 font-bold">Ücret</th>}
                {hasPerm('can_view_payments') && <th className="text-left p-3 font-bold">Rozet</th>}
                <th className="text-left p-3 font-bold">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr><td colSpan="8" className="p-6 text-center text-neutral-400">Henüz ürün yok</td></tr>
              ) : products.map((product, i) => (
                <tr key={product.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} data-testid={`product-row-${product.id}`}>
                  <td className="p-3 font-medium">{product.name}</td>
                  <td className="p-3 text-neutral-600">{product.brand}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded text-white text-xs font-bold" style={{ backgroundColor: product.score_color }}>
                      {product.score?.toFixed(1)}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded text-white text-xs font-bold" style={{ backgroundColor: product.score_color }}>
                      {product.verdict_display || (product.score >= 7 ? 'TEST BAŞARILI' : 'BEKLENTİYİ KARŞILAMADI')}
                    </span>
                  </td>
                  <td className="p-3">
                    <button onClick={() => togglePublish(product)}
                      className={`px-3 py-1 text-xs font-bold rounded ${product.published ? 'bg-[#1C3B2A] text-white' : 'bg-neutral-300 text-neutral-700'}`}
                      data-testid={`toggle-publish-${product.id}`}>
                      {product.published ? 'YAYINDA' : 'TASLAK'}
                    </button>
                    {product.is_featured && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-800">
                        AS{product.featured_order ? ` #${product.featured_order}` : ''}
                      </span>
                    )}
                  </td>
                  {hasPerm('can_view_payments') && (
                    <td className="p-3">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${product.review_fee_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {product.review_fee_status === 'paid' ? 'Ödendi' : 'Ödenmedi'}
                      </span>
                    </td>
                  )}
                  {hasPerm('can_view_payments') && (
                    <td className="p-3" data-testid={`badge-status-cell-${product.id}`}>
                      {(() => {
                        const s = computeBadgeStatus(product);
                        return (
                          <span className={`px-2 py-0.5 text-xs font-bold rounded whitespace-nowrap ${s.cls}`}>
                            {s.label}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex gap-2">
                      {hasPerm('can_edit_product') && (
                        <button onClick={() => handleEdit(product)} className="text-xs font-semibold text-blue-600 hover:underline"
                          data-testid={`edit-product-${product.id}`}>Düzenle</button>
                      )}
                      {hasPerm('can_delete') && (
                        <button onClick={() => handleDelete(product.id)} className="text-xs font-semibold text-red-600 hover:underline"
                          data-testid={`delete-product-${product.id}`}>Sil</button>
                      )}
                      {product.score >= 7 && !product.badge_active && (
                        <button
                          onClick={() => openBadgeActivateModal(product)}
                          className="text-xs font-semibold text-[#1C3B2A] hover:underline"
                          data-testid={`activate-badge-${product.id}`}
                        >
                          Güven Rozeti Aktive Et
                        </button>
                      )}
                      {product.badge_active && (
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API_URL}/api/products/${product.id}/deactivate-badge`);
                            toast.success('Rozet devre dışı bırakıldı'); fetchProducts();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Rozet deaktivasyonu başarısız'); }
                        }} className="text-xs font-semibold text-amber-600 hover:underline"
                          data-testid={`deactivate-badge-${product.id}`}>Güven Rozeti Kapat</button>
                      )}
                      {product.score >= 7 && !verifyLinks[product.id] && (
                        <button onClick={() => handleGenerateVerifyLink(product.id)}
                          className="text-xs font-semibold text-indigo-600 hover:underline"
                          data-testid={`generate-verify-${product.id}`}>Doğrulama Linki Oluştur</button>
                      )}
                      {verifyLinks[product.id] && (
                        <button onClick={() => handleCopyVerifyLink(verifyLinks[product.id])}
                          className="text-xs font-semibold text-teal-600 hover:underline"
                          data-testid={`copy-verify-${product.id}`}>Linki Kopyala</button>
                      )}
                      {verifyLinks[product.id] && (
                        <button onClick={() => openPerfModal(product)}
                          className="text-xs font-semibold text-purple-600 hover:underline"
                          data-testid={`perf-btn-${product.id}`}>Performans</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Performans Modal ── */}
      {perfModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" data-testid="perf-modal-overlay" onClick={() => setPerfModal(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="perf-modal">
            <div className="border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black tracking-tight" data-testid="perf-modal-title">Doğrulama Performansı</h3>
                <p className="text-xs text-neutral-500 mt-0.5">{perfModal.name} ({perfModal.brand})</p>
              </div>
              <button onClick={() => setPerfModal(null)} className="text-neutral-400 hover:text-neutral-700 text-xl font-bold leading-none">&times;</button>
            </div>
            <div className="px-6 py-4">
              {/* Date range tabs */}
              <div className="flex gap-2 mb-5" data-testid="perf-range-tabs">
                {[['7d', 'Son 7 gün'], ['30d', 'Son 30 gün'], ['all', 'Tüm zamanlar']].map(([val, label]) => (
                  <button key={val} onClick={() => changePerfRange(val)}
                    className={`px-3 py-1.5 text-xs font-bold rounded transition ${perfRange === val ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                    data-testid={`perf-range-${val}`}>{label}</button>
                ))}
              </div>
              {perfLoading ? (
                <p className="text-sm text-neutral-400 py-8 text-center">Yükleniyor...</p>
              ) : perfData ? (
                <div className="space-y-5">
                  {/* KPIs */}
                  <div className="grid grid-cols-3 gap-3" data-testid="perf-kpis">
                    <div className="bg-neutral-50 rounded-lg p-4 text-center border border-neutral-200">
                      <p className="text-2xl font-black text-neutral-900" data-testid="perf-views">{perfData.total_views}</p>
                      <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide mt-1">Görüntülenme</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-4 text-center border border-neutral-200">
                      <p className="text-2xl font-black text-neutral-900" data-testid="perf-clicks">{perfData.total_clicks}</p>
                      <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide mt-1">Tıklama</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-4 text-center border border-neutral-200">
                      <p className="text-2xl font-black text-neutral-900" data-testid="perf-ctr">%{perfData.ctr}</p>
                      <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide mt-1">CTR</p>
                    </div>
                  </div>
                  {/* Source breakdown */}
                  <div data-testid="perf-sources">
                    <p className="text-xs font-black uppercase text-neutral-500 mb-2 tracking-wide">Kaynak Dağılımı</p>
                    {Object.keys(perfData.source_breakdown || {}).length > 0 ? (
                      <div className="border border-neutral-200 rounded-md divide-y divide-neutral-100">
                        {Object.entries(perfData.source_breakdown).map(([key, val]) => (
                          <div key={key} className="flex justify-between items-center px-3 py-2 text-sm">
                            <span className="font-medium text-neutral-700">{key}</span>
                            <span className="font-bold text-neutral-900">{val}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-neutral-400">Henüz veri yok</p>}
                  </div>
                  {/* Destination breakdown */}
                  <div data-testid="perf-destinations">
                    <p className="text-xs font-black uppercase text-neutral-500 mb-2 tracking-wide">Hedef Dağılımı</p>
                    {Object.keys(perfData.destination_breakdown || {}).length > 0 ? (
                      <div className="border border-neutral-200 rounded-md divide-y divide-neutral-100">
                        {Object.entries(perfData.destination_breakdown).map(([key, val]) => (
                          <div key={key} className="flex justify-between items-center px-3 py-2 text-sm">
                            <span className="font-medium text-neutral-700">{key}</span>
                            <span className="font-bold text-neutral-900">{val}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-neutral-400">Henüz veri yok</p>}
                  </div>
                  {/* Copy report button */}
                  <button onClick={handleCopyReport}
                    className="w-full py-2.5 bg-neutral-900 text-white text-sm font-bold rounded hover:bg-neutral-800 transition"
                    data-testid="copy-report-btn">Raporu Kopyala</button>
                </div>
              ) : (
                <p className="text-sm text-neutral-400 py-8 text-center">Veri yüklenemedi</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TT-ROZET-001-FINAL — Badge activation date-picker modal */}
      {badgeActivateModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          data-testid="badge-activate-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !badgeActivateBusy) setBadgeActivateModal(null); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-black tracking-tight mb-1 text-neutral-900">Güven Rozeti Aktive Et</h3>
            <p className="text-xs text-neutral-500 mb-5" data-testid="badge-activate-modal-product-name">
              Ürün: <span className="font-semibold text-neutral-800">{badgeActivateModal.productName}</span>
            </p>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1 text-neutral-700">
              Geçerlilik Tarihi
            </label>
            <input
              type="date"
              value={badgeActivateDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setBadgeActivateDate(e.target.value)}
              className="w-full border-2 border-neutral-300 rounded-lg px-3 py-2 text-sm font-semibold focus:border-emerald-700 outline-none"
              data-testid="badge-activate-date-input"
            />
            <p className="text-[11px] text-neutral-500 mt-2">
              Bu tarihe kadar müşteriler doğrulama linkini görebilir. Varsayılan bugünden 30 gün sonrasıdır.
            </p>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setBadgeActivateModal(null)}
                disabled={badgeActivateBusy}
                className="px-4 py-2 text-sm font-bold rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                data-testid="badge-activate-cancel"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={confirmBadgeActivate}
                disabled={badgeActivateBusy || !badgeActivateDate}
                className="px-4 py-2 text-sm font-bold rounded-lg text-white disabled:opacity-50"
                style={{ background: '#1C3B2A' }}
                data-testid="badge-activate-confirm"
              >
                {badgeActivateBusy ? 'Aktive ediliyor…' : 'Aktive Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const SubmissionsManagement = () => {
  const { hasPerm } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [noteInput, setNoteInput] = useState('');

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      const [subs, prods] = await Promise.all([
        axios.get(`${API_URL}/api/submissions?${params.toString()}`).then(r => r.data).catch(() => []),
        axios.get(`${API_URL}/api/products`).then(r => r.data).catch(() => []),
      ]);
      setSubmissions(subs);
      setProducts(prods);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [searchTerm, statusFilter]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/submissions/${id}`, { status: newStatus, note: noteInput });
      toast.success('Durum güncellendi');
      setNoteInput('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Güncelleme başarısız'); }
  };

  const linkProduct = async (subId, productId) => {
    try {
      await axios.put(`${API_URL}/api/submissions/${subId}`, { linked_product_id: productId });
      toast.success('Ürün bağlandı');
      fetchData();
    } catch { toast.error('Bağlama başarısız'); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="submissions-title">Başvuru Yönetimi</h2>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="submissions-toolbar">
        <input type="text" placeholder="Referans no, marka, ürün veya kişi ara..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm w-72 focus:outline-none focus:border-neutral-500"
          data-testid="submission-search-input" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm bg-white"
          data-testid="submission-status-filter">
          <option value="">Tüm Durumlar</option>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <span className="text-xs text-neutral-400 ml-auto">{submissions.length} başvuru</span>
      </div>

      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Referans No</th>
                <th className="text-left p-3 font-bold">Ürün</th>
                <th className="text-left p-3 font-bold">Marka</th>
                <th className="text-left p-3 font-bold">İşletme</th>
                <th className="text-left p-3 font-bold">İletişim</th>
                <th className="text-left p-3 font-bold">Durum</th>
                <th className="text-left p-3 font-bold">Bağlı Ürün</th>
                <th className="text-left p-3 font-bold">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 ? (
                <tr><td colSpan="8" className="p-6 text-center text-neutral-400">Başvuru bulunamadı</td></tr>
              ) : submissions.map((sub, i) => (
                <React.Fragment key={sub.id}>
                  <tr className={`${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} cursor-pointer hover:bg-neutral-100 transition`}
                    onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                    data-testid={`submission-row-${sub.id}`}>
                    <td className="p-3">
                      <span className="font-mono font-bold text-[#1C3B2A] text-xs" data-testid={`ref-${sub.id}`}>{sub.reference_number || '-'}</span>
                    </td>
                    <td className="p-3 font-medium">{sub.product_name}</td>
                    <td className="p-3 text-neutral-600">{sub.brand}</td>
                    <td className="p-3 text-neutral-600 text-xs">{sub.business_name || '-'}</td>
                    <td className="p-3 text-xs">
                      <div className="font-medium">{sub.contact_name}</div>
                      <div className="text-neutral-500">{sub.contact_email}</div>
                      <div className="text-neutral-400">{sub.contact_phone}</div>
                    </td>
                    <td className="p-3">
                      <select value={sub.status} onClick={(e) => e.stopPropagation()}
                        onChange={(e) => updateStatus(sub.id, e.target.value)}
                        className="border border-neutral-300 rounded px-2 py-1 text-xs font-medium"
                        data-testid={`status-select-${sub.id}`}>
                        {Object.entries(STATUS_LABELS).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                      </select>
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <select value={sub.linked_product_id || ''} onChange={(e) => linkProduct(sub.id, e.target.value)}
                        className="border border-neutral-300 rounded px-2 py-1 text-xs">
                        <option value="">Ürün Seç...</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.brand})</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-xs text-neutral-400 whitespace-nowrap">
                      {sub.created_at ? new Date(sub.created_at).toLocaleDateString('tr-TR') : '-'}
                    </td>
                  </tr>
                  {expandedId === sub.id && (
                    <tr className="bg-neutral-50">
                      <td colSpan="8" className="p-4 border-t border-neutral-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div>
                            <p className="font-bold text-neutral-500 mb-1">Açıklama</p>
                            <p className="text-neutral-700">{sub.description || '-'}</p>
                          </div>
                          <div>
                            <p className="font-bold text-neutral-500 mb-1">Adres</p>
                            <p className="text-neutral-700">{sub.address}, {sub.city}</p>
                          </div>
                          <div>
                            <p className="font-bold text-neutral-500 mb-1">Kategori</p>
                            <p className="text-neutral-700">{sub.category}</p>
                          </div>
                          <div>
                            <p className="font-bold text-neutral-500 mb-2">Durum Geçmişi</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {(sub.status_history || []).map((h, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-[11px]">
                                  <span className="text-neutral-400 whitespace-nowrap">{h.timestamp ? new Date(h.timestamp).toLocaleDateString('tr-TR') : ''}</span>
                                  <span className="font-semibold text-neutral-700">{STATUS_LABELS[h.status] || h.status}</span>
                                  {h.note && <span className="text-neutral-400 italic">({h.note})</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Sellers (Report System) ──
const SellersManagement = () => {
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [detail, setDetail] = useState(null);
  const [assignProduct, setAssignProduct] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchSellers = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sellers`),
        axios.get(`${API_URL}/api/products`),
      ]);
      setSellers(s.data);
      setProducts(p.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSellers(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/sellers`, { name: newName.trim() });
      setNewName(''); setShowForm(false); fetchSellers();
      toast.success('Satıcı oluşturuldu');
    } catch { toast.error('Oluşturulamadı'); }
  };

  const handleRegenerate = async (sid) => {
    try {
      await axios.post(`${API_URL}/api/admin/sellers/${sid}/regenerate-token`);
      fetchSellers(); if (detail?.id === sid) loadDetail(sid);
      toast.success('Token yenilendi');
    } catch { toast.error('Token yenilenemedi'); }
  };

  const handleRevoke = async (sid) => {
    try {
      await axios.post(`${API_URL}/api/admin/sellers/${sid}/revoke-token`);
      fetchSellers(); if (detail?.id === sid) loadDetail(sid);
      toast.success('Token iptal edildi');
    } catch { toast.error('İptal edilemedi'); }
  };

  const handleAssign = async (productId, sellerId) => {
    try {
      await axios.put(`${API_URL}/api/products/${productId}`, { seller_id: sellerId });
      fetchSellers(); if (detail) loadDetail(detail.id);
      toast.success('Ürün atandı');
    } catch { toast.error('Atanamadı'); }
  };

  const loadDetail = async (sid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/sellers/${sid}`);
      setDetail(data);
    } catch { toast.error('Detay yüklenemedi'); }
  };

  const copyLink = (token, id) => {
    navigator.clipboard.writeText(`https://www.testettik.com/seller-report/${token}`);
    setCopied(id); setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-black tracking-tight" data-testid="sellers-title">Satıcı Rapor Yönetimi</h2>
        <button onClick={() => setShowForm(true)} className="bg-[#1C3B2A] text-white px-4 py-2 text-xs font-bold rounded hover:bg-[#152e21] transition" data-testid="add-seller-btn">
          Yeni Satıcı Ekle
        </button>
      </div>

      {showForm && (
        <div className="border border-neutral-200 rounded-md p-4 mb-6 bg-neutral-50">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-bold mb-1 text-neutral-600">Satıcı Adı</label>
              <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Satıcı adını girin"
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="seller-name-input" />
            </div>
            <button onClick={handleCreate} className="bg-[#1C3B2A] text-white px-4 py-2 text-xs font-bold rounded" data-testid="save-seller-btn">Kaydet</button>
            <button onClick={() => setShowForm(false)} className="text-xs text-neutral-500 hover:text-neutral-700">İptal</button>
          </div>
        </div>
      )}

      {/* Sellers List */}
      <div className="border border-neutral-200 rounded-md overflow-hidden mb-6">
        <table className="w-full text-sm" data-testid="sellers-table">
          <thead className="bg-neutral-900 text-white">
            <tr>
              <th className="text-left p-3 font-bold">Satıcı</th>
              <th className="text-left p-3 font-bold">Ürün Sayısı</th>
              <th className="text-left p-3 font-bold">Token</th>
              <th className="text-left p-3 font-bold">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sellers.length === 0 ? (
              <tr><td colSpan="4" className="p-6 text-center text-neutral-400">Henüz satıcı yok</td></tr>
            ) : sellers.map((s, i) => {
              const prodCount = products.filter(p => p.seller_id === s.id).length;
              return (
                <tr key={s.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} data-testid={`seller-row-${s.id}`}>
                  <td className="p-3 font-semibold">{s.name}</td>
                  <td className="p-3">{prodCount}</td>
                  <td className="p-3">
                    {s.token_revoked ? (
                      <span className="text-xs text-red-500 font-bold">İptal Edildi</span>
                    ) : (
                      <button onClick={() => copyLink(s.report_token, s.id)} className="text-xs font-semibold text-teal-600 hover:underline" data-testid={`copy-token-${s.id}`}>
                        {copied === s.id ? 'Kopyalandı!' : 'Linki Kopyala'}
                      </button>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => loadDetail(s.id)} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`detail-${s.id}`}>Detay</button>
                      <button onClick={() => setAssignProduct(s.id)} className="text-xs font-semibold text-purple-600 hover:underline" data-testid={`assign-${s.id}`}>Ürün Ata</button>
                      {!s.token_revoked && <button onClick={() => handleRegenerate(s.id)} className="text-xs font-semibold text-orange-600 hover:underline">Yenile</button>}
                      {!s.token_revoked && <button onClick={() => handleRevoke(s.id)} className="text-xs font-semibold text-red-500 hover:underline">İptal</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Assign Product Modal */}
      {assignProduct && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setAssignProduct(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b px-5 py-4 flex justify-between items-center">
              <h3 className="text-base font-black">Ürün Ata</h3>
              <button onClick={() => setAssignProduct(null)} className="text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-2">
              {products.filter(p => !p.seller_id || p.seller_id === assignProduct).map(p => (
                <div key={p.id} className="flex items-center justify-between border border-neutral-200 rounded p-3">
                  <div>
                    <p className="text-sm font-bold">{p.name}</p>
                    <p className="text-xs text-neutral-500">{p.brand} — Skor: {p.score}</p>
                  </div>
                  {p.seller_id === assignProduct ? (
                    <span className="text-xs font-bold text-green-600">Atanmış</span>
                  ) : (
                    <button onClick={() => { handleAssign(p.id, assignProduct); }} className="text-xs font-bold text-[#1C3B2A] hover:underline">Ata</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Seller Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b px-5 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black">{detail.name}</h3>
                <p className="text-xs text-neutral-500">Satıcı Detayı</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="border rounded p-3 bg-neutral-50">
                <p className="text-xs font-bold text-neutral-500 mb-1">Rapor Linki</p>
                {detail.token_revoked ? (
                  <p className="text-xs text-red-500 font-bold">Token iptal edildi</p>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-mono break-all flex-1">{`https://www.testettik.com/seller-report/${detail.report_token}`}</p>
                    <button onClick={() => copyLink(detail.report_token, 'detail')} className="text-xs font-bold text-teal-600 hover:underline flex-shrink-0">
                      {copied === 'detail' ? 'Kopyalandı!' : 'Kopyala'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold text-neutral-500 mb-2">Ürünler ({detail.products?.length || 0})</p>
                <div className="space-y-2">
                  {(detail.products || []).map(p => (
                    <div key={p.id} className="border rounded p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold">{p.name}</p>
                        <p className="text-xs text-neutral-500">{p.brand} — Skor: {p.score}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${p.badge_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
                        {p.badge_active ? 'Rozet Aktif' : 'Rozet Yok'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Vendors ──
const VendorsManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/vendors`).then(r => setVendors(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;
  return (
    <div className="p-6">
      <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="vendors-title">Satıcı Yönetimi</h2>
      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Ad</th>
                <th className="text-left p-3 font-bold">E-posta</th>
                <th className="text-left p-3 font-bold">Telefon</th>
                <th className="text-left p-3 font-bold">Marka</th>
                <th className="text-left p-3 font-bold">Başvuru</th>
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <tr><td colSpan="5" className="p-6 text-center text-neutral-400">Henüz satıcı yok</td></tr>
              ) : vendors.map((v, i) => (
                <tr key={v.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}>
                  <td className="p-3 font-medium">{v.name}</td>
                  <td className="p-3 text-neutral-600">{v.email}</td>
                  <td className="p-3 text-neutral-600">{v.phone || '-'}</td>
                  <td className="p-3">{v.brand}</td>
                  <td className="p-3 text-center font-bold">{v.submission_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Payments (restricted) ──
const PaymentsManagement = () => {
  const { hasPerm } = useAuth();
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '', payment_type: 'review_fee', amount: '', status: 'unpaid', notes: '',
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/payments`).then(r => r.data).catch(() => []),
      axios.get(`${API_URL}/api/products`).then(r => r.data).catch(() => []),
    ]).then(([pays, prods]) => { setPayments(pays); setProducts(prods); }).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/payments`, { ...formData, amount: parseFloat(formData.amount) });
      toast.success('Ödeme kaydı oluşturuldu');
      setShowForm(false);
      setFormData({ product_id: '', payment_type: 'review_fee', amount: '', status: 'unpaid', notes: '' });
      const { data } = await axios.get(`${API_URL}/api/payments`);
      setPayments(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const toggleStatus = async (payment) => {
    try {
      await axios.put(`${API_URL}/api/payments/${payment.id}?status=${payment.status === 'paid' ? 'unpaid' : 'paid'}`);
      toast.success('Durum güncellendi');
      const { data } = await axios.get(`${API_URL}/api/payments`);
      setPayments(data);
    } catch { toast.error('Güncelleme başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!hasPerm('can_delete')) { toast.error('Silme yetkiniz yok'); return; }
    if (!window.confirm('Bu ödeme kaydını silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${id}`);
      toast.success('Ödeme silindi');
      const { data } = await axios.get(`${API_URL}/api/payments`);
      setPayments(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Silme başarısız'); }
  };

  const getProductName = (pid) => {
    const p = products.find(x => x.id === pid);
    return p ? `${p.name} (${p.brand})` : pid || '-';
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-black tracking-tight" data-testid="payments-title">Ödeme Takibi</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-neutral-900 text-white px-5 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition"
          data-testid="add-payment-button">+ Yeni Ödeme</button>
      </div>

      {showForm && (
        <div className="bg-neutral-50 border border-neutral-200 rounded-md p-6 mb-6">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Ürün</label>
              <select value={formData.product_id} onChange={(e) => setFormData(p => ({ ...p, product_id: e.target.value }))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm">
                <option value="">Ürün Seç...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.brand})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Ödeme Tipi</label>
              <select value={formData.payment_type} onChange={(e) => setFormData(p => ({ ...p, payment_type: e.target.value }))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm">
                <option value="review_fee">İnceleme Ücreti</option>
                <option value="badge_subscription">Rozet Abonelik</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Tutar (TL)</label>
              <input type="number" value={formData.amount} onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                required min="0" step="0.01" className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1 text-neutral-600">Durum</label>
              <select value={formData.status} onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm">
                <option value="unpaid">Ödenmedi</option>
                <option value="paid">Ödendi</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold mb-1 text-neutral-600">Not</label>
              <input type="text" value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="bg-neutral-900 text-white px-6 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition">Kaydet</button>
              <button type="button" onClick={() => setShowForm(false)}
                className="border border-neutral-300 px-6 py-2.5 text-sm font-bold rounded hover:bg-neutral-100 transition">İptal</button>
            </div>
          </form>
        </div>
      )}

      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Ürün</th>
                <th className="text-left p-3 font-bold">Tip</th>
                <th className="text-left p-3 font-bold">Tutar</th>
                <th className="text-left p-3 font-bold">Durum</th>
                <th className="text-left p-3 font-bold">Not</th>
                <th className="text-left p-3 font-bold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-neutral-400">Henüz ödeme kaydı yok</td></tr>
              ) : payments.map((payment, i) => (
                <tr key={payment.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}>
                  <td className="p-3 font-medium">{getProductName(payment.product_id)}</td>
                  <td className="p-3">{payment.payment_type === 'review_fee' ? 'İnceleme Ücreti' : 'Rozet Abonelik'}</td>
                  <td className="p-3 font-bold">₺{payment.amount?.toFixed(2)}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${payment.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {payment.status === 'paid' ? 'Ödendi' : 'Ödenmedi'}
                    </span>
                  </td>
                  <td className="p-3 text-neutral-500 text-xs">{payment.notes || '-'}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => toggleStatus(payment)} className="text-xs font-semibold text-blue-600 hover:underline">Durumu Değiştir</button>
                      {hasPerm('can_delete') && (
                        <button onClick={() => handleDelete(payment.id)} className="text-xs font-semibold text-red-600 hover:underline">Sil</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── User Management (Super Admin Only) ──
const ROLE_PRESETS = {
  super_admin: {
    can_add_product: true, can_edit_product: true, can_score_product: true,
    can_publish_product: true, can_access_embed: true, can_view_payments: true,
    can_delete: true, can_create_vendor_users: true, can_manage_homepage: true,
    can_manage_badges: true, can_manage_premium: true, can_manage_users: true,
    can_view_submissions: true, can_update_submission_status: true,
  },
  admin: {
    can_add_product: true, can_edit_product: true, can_score_product: true,
    can_publish_product: true, can_access_embed: true, can_view_payments: false,
    can_delete: false, can_create_vendor_users: true, can_manage_homepage: false,
    can_manage_badges: false, can_manage_premium: false, can_manage_users: false,
    can_view_submissions: true, can_update_submission_status: true,
  },
  vendor: {
    can_add_product: false, can_edit_product: false, can_score_product: false,
    can_publish_product: false, can_access_embed: true, can_view_payments: false,
    can_delete: false, can_create_vendor_users: false, can_manage_homepage: false,
    can_manage_badges: false, can_manage_premium: false, can_manage_users: false,
    can_view_submissions: false, can_update_submission_status: false,
  },
};

const PERM_CHECKBOX_LABELS = [
  { key: 'can_add_product', label: 'Ürün oluşturabilir' },
  { key: 'can_edit_product', label: 'Ürün düzenleyebilir' },
  { key: 'can_score_product', label: 'Skor girebilir' },
  { key: 'can_publish_product', label: 'Yayınlayabilir' },
  { key: 'can_view_payments', label: 'Ödeme bilgilerini görebilir' },
  { key: 'can_delete', label: 'Silme yetkisi' },
  { key: 'can_create_vendor_users', label: 'Satıcı kullanıcısı oluşturabilir' },
  { key: 'can_access_embed', label: 'Embed erişimi' },
  { key: 'can_manage_homepage', label: 'Ana sayfaya ürün atayabilir' },
  { key: 'can_manage_badges', label: 'Güven Rozeti yönetimi' },
  { key: 'can_manage_premium', label: 'Premium yönetimi' },
  { key: 'can_manage_users', label: 'Kullanıcı yönetimi' },
  { key: 'can_view_submissions', label: 'Başvuruları görüntüleme' },
  { key: 'can_update_submission_status', label: 'Başvuru durumu güncelleme' },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'admin', vendor_brand: '' });
  const [perms, setPerms] = useState({ ...ROLE_PRESETS.admin });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/users`); setUsers(data); }
    catch { toast.error('Kullanıcılar yüklenemedi'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', name: '', role: 'admin', vendor_brand: '' });
    setPerms({ ...ROLE_PRESETS.admin });
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setFormData({ email: u.email, password: '', name: u.name, role: u.role, vendor_brand: u.vendor_brand || '' });
    setPerms({ ...ROLE_PRESETS[u.role], ...(u.permissions || {}) });
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingUser(null); };

  const handleRoleChange = (newRole) => {
    setFormData(p => ({ ...p, role: newRole }));
    setPerms({ ...ROLE_PRESETS[newRole] });
  };

  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = { name: formData.name, role: formData.role, vendor_brand: formData.vendor_brand || null, permissions: perms };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, payload);
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${API_URL}/api/users`, { ...formData, permissions: perms });
        toast.success('Kullanıcı oluşturuldu');
      }
      closeModal();
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const toggleActive = async (u) => {
    if (u.role === 'super_admin') return;
    try {
      await axios.put(`${API_URL}/api/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? 'Kullanıcı devre dışı bırakıldı' : 'Kullanıcı aktifleştirildi');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  const q = searchTerm.toLowerCase();
  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (statusFilter === 'active' && u.active === false) return false;
    if (statusFilter === 'disabled' && u.active !== false) return false;
    if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    return true;
  });
  const hasFilters = searchTerm || roleFilter !== 'all' || statusFilter !== 'all';
  const clearFilters = () => { setSearchTerm(''); setRoleFilter('all'); setStatusFilter('all'); };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-black tracking-tight" data-testid="users-title">Kullanıcı Yönetimi</h2>
        <button onClick={openCreate}
          className="bg-neutral-900 text-white px-5 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition"
          data-testid="add-user-button">+ Yeni Kullanıcı</button>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="user-filters-toolbar">
        <input
          type="text" placeholder="Ad veya e-posta ile ara..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm w-64 focus:outline-none focus:border-neutral-500 transition"
          data-testid="user-search-input"
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-neutral-500 transition"
          data-testid="user-role-filter">
          <option value="all">Tüm Roller</option>
          <option value="super_admin">Süper Yönetici</option>
          <option value="admin">Standart Yönetici</option>
          <option value="vendor">Satıcılar</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-neutral-500 transition"
          data-testid="user-status-filter">
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="disabled">Devre Dışı</option>
        </select>
        {hasFilters && (
          <button onClick={clearFilters}
            className="text-xs font-bold text-neutral-500 hover:text-neutral-900 border border-neutral-300 rounded px-3 py-2 transition"
            data-testid="user-clear-filters">Temizle</button>
        )}
        <span className="text-xs text-neutral-400 ml-auto">{filtered.length} kullanıcı</span>
      </div>

      {/* User Table */}
      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Ad Soyad</th>
                <th className="text-left p-3 font-bold">E-posta</th>
                <th className="text-left p-3 font-bold">Rol</th>
                <th className="text-left p-3 font-bold">Durum</th>
                <th className="text-left p-3 font-bold">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center">
                  <p className="text-sm font-semibold text-neutral-400 mb-2">Kullanıcı bulunamadı</p>
                  {hasFilters && <button onClick={clearFilters} className="text-xs font-bold text-neutral-500 hover:text-neutral-900 underline" data-testid="user-empty-clear">Filtreleri temizle</button>}
                </td></tr>
              ) : filtered.map((u, i) => (
                <tr key={u.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-semibold text-neutral-900">{u.name}</td>
                  <td className="p-3 text-neutral-600">{u.email}</td>
                  <td className="p-3">
                    <span className={`px-2.5 py-1 text-[11px] font-bold rounded ${
                      u.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                      u.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {u.role === 'super_admin' ? 'SÜPER YÖNETİCİ' : u.role === 'admin' ? 'STANDART YÖNETİCİ' : 'SATICI'}
                    </span>
                    {u.role === 'vendor' && u.vendor_brand && (
                      <span className="text-[10px] text-neutral-400 ml-1.5">({u.vendor_brand})</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`px-2.5 py-1 text-[11px] font-bold rounded ${u.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.active !== false ? 'Aktif' : 'Devre Dışı'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {u.role !== 'super_admin' && (
                        <>
                          <button onClick={() => openEdit(u)}
                            className="px-3 py-1.5 text-xs font-bold bg-neutral-900 text-white rounded hover:bg-neutral-700 transition"
                            data-testid={`edit-user-${u.id}`}>Düzenle</button>
                          <button onClick={() => toggleActive(u)}
                            className={`px-3 py-1.5 text-xs font-bold rounded transition ${
                              u.active !== false
                                ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                            }`}
                            data-testid={`toggle-active-${u.id}`}>
                            {u.active !== false ? 'Devre Dışı Bırak' : 'Aktifleştir'}
                          </button>
                        </>
                      )}
                      {u.role === 'super_admin' && (
                        <span className="text-[10px] text-neutral-400 italic">Düzenlenemez</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Overlay */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" data-testid="user-modal-overlay" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-black tracking-tight" data-testid="modal-title">
                {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
              </h3>
              <button onClick={closeModal} className="text-neutral-400 hover:text-neutral-700 text-xl font-bold leading-none">&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">Ad Soyad</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  required className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition" data-testid="user-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">E-posta</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  required disabled={!!editingUser} className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm disabled:bg-neutral-100 focus:outline-none focus:border-neutral-500 transition" data-testid="user-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">
                  {editingUser ? 'Yeni Şifre (boş bırakılabilir)' : 'Şifre'}
                </label>
                <input type="password" value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                  required={!editingUser} className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition" data-testid="user-password-input" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">Rol</label>
                <select value={formData.role} onChange={(e) => handleRoleChange(e.target.value)}
                  disabled={editingUser?.role === 'super_admin'}
                  className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm disabled:bg-neutral-100 focus:outline-none focus:border-neutral-500 transition" data-testid="user-role-select">
                  {editingUser?.role === 'super_admin' && <option value="super_admin">Süper Yönetici</option>}
                  <option value="admin">Standart Yönetici</option>
                  <option value="vendor">Satıcılar</option>
                </select>
              </div>
              {formData.role === 'vendor' && (
                <div>
                  <label className="block text-xs font-bold mb-1 text-neutral-600">Marka Adı</label>
                  <input type="text" value={formData.vendor_brand} onChange={(e) => setFormData(p => ({ ...p, vendor_brand: e.target.value }))}
                    required className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition" data-testid="user-brand-input" />
                </div>
              )}

              {/* Permissions checkboxes */}
              <div className="border-t border-neutral-200 pt-4 mt-2">
                <p className="text-xs font-black uppercase text-neutral-500 mb-3 tracking-wide">İzinler</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PERM_CHECKBOX_LABELS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2.5 px-3 py-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer transition select-none" data-testid={`perm-checkbox-${key}`}>
                      <input type="checkbox" checked={!!perms[key]} onChange={() => togglePerm(key)}
                        className="w-4 h-4 rounded border-neutral-300 accent-neutral-900" />
                      <span className="text-xs font-semibold text-neutral-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-3 border-t border-neutral-200 mt-2">
                <button type="submit"
                  className="flex-1 bg-neutral-900 text-white px-6 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition"
                  data-testid="save-user-button">
                  {editingUser ? 'Kaydet' : 'Kullanıcı Oluştur'}
                </button>
                <button type="button" onClick={closeModal}
                  className="px-6 py-2.5 text-sm font-bold rounded border border-neutral-300 hover:bg-neutral-100 transition">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Homepage Management (15 Slots) ──
const TOTAL_SLOTS = 15;
const HomepageManagement = () => {
  const { hasPerm } = useAuth();
  const [products, setProducts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slotSelections, setSlotSelections] = useState({});
  const [dateSelections, setDateSelections] = useState({});
  const [confirmSlot, setConfirmSlot] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const canManage = hasPerm('can_manage_homepage');

  const fetchData = async () => {
    try {
      const [prodRes, assignRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/homepage/assignments`),
      ]);
      setProducts(prodRes.data);
      setAssignments(assignRes.data);
    } catch { toast.error('Veriler yüklenemedi'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const isEligible = (p) => p.score >= 7 && p.published && p.badge_active;
  const eligibilityReason = (p) => {
    if (p.score < 7) return 'Puan 7\'nin altında';
    if (!p.published) return 'Yayında değil';
    if (!p.badge_active) return 'Güven Rozeti aktif değil';
    return '';
  };

  const activeAssignments = assignments.filter(a => a.status === 'active');
  const occupiedSlots = activeAssignments.reduce((m, a) => { m[a.homepage_slot] = a; return m; }, {});
  const q = searchTerm.toLowerCase();
  const filtered = products.filter(p => {
    if (eligibleOnly && !isEligible(p)) return false;
    if (q && !p.name.toLowerCase().includes(q) && !p.brand.toLowerCase().includes(q)) return false;
    return true;
  });

  const handleAssign = (productId) => {
    const slot = parseInt(slotSelections[productId]);
    const date = dateSelections[productId];
    if (!slot || !date) { toast.error('Sıra ve kaldırma tarihi zorunludur'); return; }
    if (occupiedSlots[slot]) { setConfirmSlot({ productId, slot, date, existing: occupiedSlots[slot] }); return; }
    doAssign(productId, slot, date);
  };

  const doAssign = async (productId, slot, date) => {
    try {
      await axios.post(`${API_URL}/api/homepage/assignments`, { product_id: productId, homepage_slot: slot, removal_date: date });
      toast.success('Ürün ana sayfaya atandı');
      setSlotSelections(p => ({ ...p, [productId]: '' }));
      setDateSelections(p => ({ ...p, [productId]: '' }));
      setConfirmSlot(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Atama başarısız'); }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await axios.delete(`${API_URL}/api/homepage/assignments/${removeTarget.id}`);
      toast.success('Ürün ana sayfadan kaldırıldı');
      setRemoveTarget(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kaldırma başarısız'); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="homepage-mgmt-title">Ana Sayfa Yönetimi</h2>

      {/* Slot Grid: 5 rows x 3 cols = 15 slots */}
      <div className="mb-8">
        <h3 className="text-sm font-black uppercase text-neutral-500 tracking-wide mb-3">Premium Yerleşim Yuvaları</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from({ length: TOTAL_SLOTS }, (_, i) => i + 1).map(slot => {
            const a = occupiedSlots[slot];
            return (
              <div key={slot} className={`border rounded-md p-4 ${a ? 'border-[#1C3B2A] bg-[#1C3B2A]/5' : 'border-dashed border-neutral-300 bg-neutral-50'}`} data-testid={`slot-card-${slot}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-black text-neutral-400">{slot}. Yuva</span>
                  {a && canManage && (
                    <button onClick={() => setRemoveTarget(a)} className="text-[10px] font-bold text-red-500 hover:underline" data-testid={`remove-slot-${slot}`}>Kaldır</button>
                  )}
                </div>
                {a ? (
                  <>
                    <p className="text-sm font-black text-neutral-900 leading-tight">{a.product_name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{a.seller_name || a.brand}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="px-2 py-0.5 text-xs font-bold rounded text-white bg-[#1C3B2A]">{a.score?.toFixed(1)}</span>
                      <span className="text-[10px] text-neutral-400">Bitiş: {a.removal_date ? new Date(a.removal_date).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-neutral-400 italic mt-1">Boş yuva.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="hp-search-toolbar">
        <input type="text" placeholder="Satıcı veya ürün adına göre ara..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-neutral-300 rounded px-3 py-2 text-sm w-72 focus:outline-none focus:border-neutral-500 transition"
          data-testid="hp-search-input" />
        <div className="flex gap-0 border border-neutral-300 rounded overflow-hidden">
          <button onClick={() => setEligibleOnly(false)}
            className={`px-4 py-2 text-xs font-bold transition ${!eligibleOnly ? 'bg-neutral-900 text-white' : 'bg-white hover:bg-neutral-50'}`}
            data-testid="hp-filter-all">Tüm Ürünler</button>
          <button onClick={() => setEligibleOnly(true)}
            className={`px-4 py-2 text-xs font-bold border-l border-neutral-300 transition ${eligibleOnly ? 'bg-[#1C3B2A] text-white' : 'bg-white hover:bg-neutral-50'}`}
            data-testid="hp-filter-eligible">Yalnızca Uygun Olanlar</button>
        </div>
        <span className="text-xs text-neutral-400 ml-auto">{filtered.length} ürün</span>
      </div>

      {/* Products Table */}
      <div className="border border-neutral-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="hp-products-table">
            <thead className="bg-neutral-900 text-white">
              <tr>
                <th className="text-left p-3 font-bold">Ürün Adı</th>
                <th className="text-left p-3 font-bold">Satıcı</th>
                <th className="text-left p-3 font-bold">Puan</th>
                <th className="text-left p-3 font-bold">Sonuç</th>
                <th className="text-left p-3 font-bold">Ödeme</th>
                <th className="text-left p-3 font-bold">Yayın</th>
                <th className="text-left p-3 font-bold">Yuva</th>
                <th className="text-left p-3 font-bold">Kaldırma Tarihi</th>
                <th className="text-left p-3 font-bold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="p-6 text-center text-neutral-400">Ürün bulunamadı</td></tr>
              ) : filtered.map((p, i) => {
                const eligible = isEligible(p);
                const reason = eligibilityReason(p);
                const alreadyAssigned = activeAssignments.some(a => a.product_id === p.id);
                return (
                  <tr key={p.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} ${!eligible ? 'opacity-50' : ''}`} data-testid={`hp-row-${p.id}`}>
                    <td className="p-3 font-semibold">{p.name}</td>
                    <td className="p-3 text-neutral-600">{p.brand}</td>
                    <td className="p-3"><span className="px-2 py-1 text-xs font-bold rounded text-white" style={{ backgroundColor: p.score_color }}>{p.score?.toFixed(1)}</span></td>
                    <td className="p-3"><span className={`text-xs font-bold ${p.score >= 7 ? 'text-[#1C3B2A]' : 'text-[#A63333]'}`}>{p.verdict_display || (p.score >= 7 ? 'Başarılı' : 'Karşılamadı')}</span></td>
                    <td className="p-3"><span className={`px-2 py-1 text-[11px] font-bold rounded ${p.review_fee_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>{p.review_fee_status === 'paid' ? 'Ödendi' : 'Ödenmedi'}</span></td>
                    <td className="p-3"><span className={`px-2 py-1 text-[11px] font-bold rounded ${p.published ? 'bg-green-100 text-green-800' : 'bg-neutral-200 text-neutral-600'}`}>{p.published ? 'Yayında' : 'Taslak'}</span></td>
                    <td className="p-3">
                      {eligible && !alreadyAssigned && canManage ? (
                        <select value={slotSelections[p.id] || ''} onChange={(e) => setSlotSelections(s => ({ ...s, [p.id]: e.target.value }))}
                          className="border border-neutral-300 rounded px-2 py-1 text-xs w-28" data-testid={`slot-select-${p.id}`}>
                          <option value="">Seçiniz</option>
                          {Array.from({ length: TOTAL_SLOTS }, (_, i) => i + 1).map(s => (
                            <option key={s} value={s}>{s}. Yuva{occupiedSlots[s] ? ' (Dolu)' : ''}</option>
                          ))}
                        </select>
                      ) : alreadyAssigned ? (
                        <span className="text-xs font-bold text-[#1C3B2A]">Atanmış</span>
                      ) : (
                        <span className="text-[10px] text-red-600 font-medium">{reason}</span>
                      )}
                    </td>
                    <td className="p-3">
                      {eligible && !alreadyAssigned && canManage ? (
                        <input type="date" value={dateSelections[p.id] || ''} onChange={(e) => setDateSelections(s => ({ ...s, [p.id]: e.target.value }))}
                          className="border border-neutral-300 rounded px-2 py-1 text-xs" data-testid={`date-select-${p.id}`} />
                      ) : <span className="text-neutral-300">-</span>}
                    </td>
                    <td className="p-3">
                      {eligible && !alreadyAssigned && canManage && (
                        <button onClick={() => handleAssign(p.id)}
                          disabled={!slotSelections[p.id] || !dateSelections[p.id]}
                          className="px-3 py-1.5 text-xs font-bold bg-neutral-900 text-white rounded hover:bg-neutral-700 transition disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`assign-btn-${p.id}`}>Ata</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slot Conflict Modal */}
      {confirmSlot && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setConfirmSlot(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="slot-conflict-modal">
            <h3 className="text-lg font-black tracking-tight mb-3">Bu yuva dolu.</h3>
            <p className="text-sm text-neutral-500 mb-6">
              <strong>{confirmSlot.slot}. Yuva</strong> şu anda <strong>{occupiedSlots[confirmSlot.slot]?.product_name}</strong> tarafından kullanılıyor.
              Mevcut ürünü kaldırıp bunu yerleştirmek istediğinize emin misiniz?
            </p>
            <div className="flex gap-3">
              <button onClick={() => doAssign(confirmSlot.productId, confirmSlot.slot, confirmSlot.date)}
                className="flex-1 bg-red-600 text-white px-5 py-2.5 text-sm font-bold rounded hover:bg-red-700 transition" data-testid="confirm-replace-btn">Evet, Değiştir</button>
              <button onClick={() => setConfirmSlot(null)} className="px-5 py-2.5 text-sm font-bold rounded border border-neutral-300 hover:bg-neutral-100 transition">İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Modal */}
      {removeTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setRemoveTarget(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="remove-confirm-modal">
            <h3 className="text-lg font-black tracking-tight mb-3">Bu ürünü ana sayfadan kaldırmak istediğinize emin misiniz?</h3>
            <p className="text-sm text-neutral-500 mb-6"><strong>{removeTarget.product_name}</strong> ({removeTarget.homepage_slot}. Yuva) ana sayfadan kaldırılacak.</p>
            <div className="flex gap-3">
              <button onClick={handleRemove} className="flex-1 bg-red-600 text-white px-5 py-2.5 text-sm font-bold rounded hover:bg-red-700 transition" data-testid="confirm-remove-btn">Evet, Kaldır</button>
              <button onClick={() => setRemoveTarget(null)} className="px-5 py-2.5 text-sm font-bold rounded border border-neutral-300 hover:bg-neutral-100 transition">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Site Settings (Super Admin Only) ──
const SiteSettings = () => {
  const [settings, setSettings] = useState({ whatsapp_number: '', whatsapp_message: '', instagram_url: '', tiktok_url: '', x_url: '', youtube_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`).then(({ data }) => setSettings(s => ({ ...s, ...data }))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings);
      toast.success('Ayarlar kaydedildi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme başarısız'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-neutral-500">Yükleniyor...</div>;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="settings-title">Site Ayarları</h2>
      <div className="max-w-lg">
        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">WhatsApp Numarası</label>
            <input type="text" value={settings.whatsapp_number}
              onChange={(e) => setSettings(s => ({ ...s, whatsapp_number: e.target.value }))}
              className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              placeholder="+905325194229" data-testid="settings-whatsapp-number" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">WhatsApp Mesajı</label>
            <textarea value={settings.whatsapp_message}
              onChange={(e) => setSettings(s => ({ ...s, whatsapp_message: e.target.value }))}
              rows="3" className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              data-testid="settings-whatsapp-message" />
          </div>
          <hr className="my-6 border-neutral-200" />
          <h3 className="text-sm font-bold text-neutral-700 mb-4" data-testid="settings-social-heading">Sosyal Medya Linkleri</h3>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">Instagram URL</label>
            <input type="text" value={settings.instagram_url || ''}
              onChange={(e) => setSettings(s => ({ ...s, instagram_url: e.target.value }))}
              className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              placeholder="https://instagram.com/..." data-testid="settings-instagram-url" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">TikTok URL</label>
            <input type="text" value={settings.tiktok_url || ''}
              onChange={(e) => setSettings(s => ({ ...s, tiktok_url: e.target.value }))}
              className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              placeholder="https://tiktok.com/@..." data-testid="settings-tiktok-url" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">X (Twitter) URL</label>
            <input type="text" value={settings.x_url || ''}
              onChange={(e) => setSettings(s => ({ ...s, x_url: e.target.value }))}
              className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              placeholder="https://x.com/..." data-testid="settings-x-url" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">YouTube URL</label>
            <input type="text" value={settings.youtube_url || ''}
              onChange={(e) => setSettings(s => ({ ...s, youtube_url: e.target.value }))}
              className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm"
              placeholder="https://youtube.com/@..." data-testid="settings-youtube-url" />
          </div>
          <button type="submit" disabled={saving}
            className="bg-neutral-900 text-white px-8 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition disabled:opacity-50"
            data-testid="settings-save-button">
            {saving ? 'KAYDEDİLİYOR...' : 'Kaydet'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── My Account ──
const MyAccount = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', current_password: '', new_password: '', new_password_confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setFormData(f => ({ ...f, name: user.name || '', email: user.email || '' }));
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.current_password) { toast.error('Mevcut şifrenizi girmelisiniz'); return; }
    if (formData.new_password && formData.new_password !== formData.new_password_confirm) {
      toast.error('Yeni şifreler eşleşmiyor'); return;
    }
    if (formData.new_password && formData.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır'); return;
    }
    setSaving(true);
    try {
      const payload = { name: formData.name, email: formData.email, current_password: formData.current_password };
      if (formData.new_password) payload.new_password = formData.new_password;
      const { data } = await axios.put(`${API_URL}/api/auth/account`, payload);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      if (setUser) setUser(prev => ({ ...prev, name: formData.name, email: formData.email }));
      toast.success(data.message || 'Hesap bilgileri güncellendi');
      setFormData(f => ({ ...f, current_password: '', new_password: '', new_password_confirm: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="account-title">Hesabım</h2>
      <div className="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">Ad Soyad</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
              required className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition"
              data-testid="account-name-input" />
          </div>
          <div>
            <label className="block text-xs font-bold mb-1 text-neutral-600">E-posta</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
              required className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition"
              data-testid="account-email-input" />
          </div>
          <div className="border-t border-neutral-200 pt-5 mt-2">
            <p className="text-xs font-black uppercase text-neutral-500 mb-4 tracking-wide">Şifre Değişikliği</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">Mevcut Şifre *</label>
                <input type="password" value={formData.current_password} onChange={(e) => setFormData(f => ({ ...f, current_password: e.target.value }))}
                  required className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition"
                  data-testid="account-current-password" />
                <p className="text-[10px] text-neutral-400 mt-1">Herhangi bir değişiklik için mevcut şifreniz gereklidir</p>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">Yeni Şifre</label>
                <input type="password" value={formData.new_password} onChange={(e) => setFormData(f => ({ ...f, new_password: e.target.value }))}
                  className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition"
                  data-testid="account-new-password" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 text-neutral-600">Yeni Şifre Tekrar</label>
                <input type="password" value={formData.new_password_confirm} onChange={(e) => setFormData(f => ({ ...f, new_password_confirm: e.target.value }))}
                  className="w-full border border-neutral-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-neutral-500 transition"
                  data-testid="account-confirm-password" />
              </div>
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="bg-neutral-900 text-white px-8 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition disabled:opacity-50"
            data-testid="account-save-button">
            {saving ? 'GÜNCELLENİYOR...' : 'Güncelle'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Main Admin Dashboard ──
const AdminDashboard = () => {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => { await logout(); navigate('/'); };

  const navItems = [
    { path: '/admin', label: 'Ürünler', testId: 'nav-products', always: true },
    { path: '/admin/submissions', label: 'Başvurular', testId: 'nav-submissions', always: true },
    { path: '/admin/sellers', label: 'Satıcı Rapor', testId: 'nav-sellers', superOnly: true },
    { path: '/admin/users', label: 'Kullanıcılar', testId: 'nav-users', superOnly: true },
    { path: '/admin/homepage', label: 'Ana Sayfa Yönetimi', testId: 'nav-homepage', perm: 'can_manage_homepage' },
    { path: '/admin/procedures', label: 'Test Prosedürleri', testId: 'nav-procedures', superOnly: true },
    { path: '/admin/contact-messages', label: 'İletişim Mesajları', testId: 'nav-contact-messages', superOnly: true },
    { path: '/admin/settings', label: 'Ayarlar', testId: 'nav-settings', superOnly: true },
    { path: '/admin/dashboard', label: 'Dashboard', testId: 'nav-dashboard', always: true },
    { path: '/admin/account', label: 'Hesabım', testId: 'nav-account', always: true },
  ];

  const visibleNav = navItems.filter(item => {
    if (item.always) return true;
    if (item.superOnly) return user?.role === 'super_admin';
    if (item.perm) return hasPerm(item.perm);
    return true;
  });

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="bg-[#1C3B2A] text-white">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <LogoWhite height={20} />
            <span className="text-xs text-white/60 font-medium">Admin Panel</span>
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
              user?.role === 'super_admin' ? 'bg-white/20 text-white' : 'bg-white/15 text-white/80'
            }`}>
              {user?.role === 'super_admin' ? 'SÜPER YÖNETİCİ' : 'YÖNETİCİ'}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/60">{user?.email}</span>
            <button onClick={handleLogout} className="text-xs font-semibold text-white/70 hover:text-white transition"
              data-testid="logout-button">Çıkış</button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="w-56 border-r border-neutral-200 min-h-[calc(100vh-56px)] bg-neutral-50">
          <nav className="p-4 space-y-1">
            {visibleNav.map(item => (
              <Link key={item.path} to={item.path}
                className={`block py-2.5 px-4 rounded-lg text-sm font-medium transition ${
                  isActive(item.path) ? 'bg-[#1C3B2A] text-white' : 'text-[#333] hover:bg-neutral-200'
                }`}
                data-testid={item.testId}>{item.label}</Link>
            ))}
            <div className="border-t border-neutral-200 mt-4 pt-4">
              <Link to="/" className="block py-2.5 px-4 rounded-lg text-sm font-medium text-[#666] hover:bg-neutral-200 transition">&larr; Ana Sayfa</Link>
            </div>
          </nav>
        </aside>

        <main className="flex-1 min-h-[calc(100vh-56px)]">
          <Routes>
            <Route path="/" element={<ProductsManagement />} />
            <Route path="/submissions" element={<SubmissionsManagement />} />
            <Route path="/vendors" element={<VendorsManagement />} />
            {user?.role === 'super_admin' && <Route path="/sellers" element={<SellersManagement />} />}
            {hasPerm('can_view_payments') && <Route path="/payments" element={<PaymentsManagement />} />}
            {user?.role === 'super_admin' && <Route path="/users" element={<UserManagement />} />}
            {(user?.role === 'super_admin' || hasPerm('can_manage_homepage')) && <Route path="/homepage" element={<HomepageManagement />} />}
            {user?.role === 'super_admin' && <Route path="/procedures" element={<TestProceduresManagement />} />}
            {user?.role === 'super_admin' && <Route path="/contact-messages" element={<ContactMessagesManagement />} />}
            {user?.role === 'super_admin' && <Route path="/settings" element={<SiteSettings />} />}
            <Route path="/dashboard" element={<AnalyticsDashboard />} />
            <Route path="/account" element={<MyAccount />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
